import { apiClient } from './client';

// Mirrors packages/api/src/routes/pilots.ts and modules/outreach-pilots.

export const STAGES = ['not_started', 'in_sequence', 'replied', 'not_interested', 'interested', 'meeting'] as const;
export type Stage = (typeof STAGES)[number];

/** SDRs write English only; AEs also write Dutch, French and Spanish. */
export const REP_ROLES = ['sdr', 'ae'] as const;
export type RepRole = (typeof REP_ROLES)[number];

/** `off`: a lemlist seat that is not a pilot rep; hidden from pickers, set in Team roles. */
export type RepSetting = RepRole | 'off';
export interface Rep { email: string; name: string | null; role?: RepRole | null; off?: boolean }

/** Outreach language; nl_fr = Belgium, not decided yet. SDRs write English only. */
export const LANGUAGES = ['en', 'nl', 'fr', 'de', 'es', 'nl_fr'] as const;
export type Language = (typeof LANGUAGES)[number];
export const CHOOSABLE_LANGUAGES = ['en', 'nl', 'fr', 'de', 'es'] as const;
export type ChoosableLanguage = (typeof CHOOSABLE_LANGUAGES)[number];

export interface Touches {
  linkedinInvites: number;
  linkedinAccepted: boolean;
  linkedinMessages: number;
  emails: number;
  calls: number;          // through lemlist
  manualCall: boolean;    // ticked by hand
  manualMeeting: boolean; // ticked by hand
}

export type Flag =
  | { kind: 'other_sender'; senders: string[] }
  | { kind: 'not_started'; days: number }
  | { kind: 'failures'; count: number; lastReason: string | null }
  | { kind: 'wrong_language'; language: Language; rep: string };

export interface TimelineEvent { at: string; type: string; sender: string | null; campaign: string | null; detail: string | null }

export interface PilotPerson {
  id: string;
  fullName: string;
  jobTitle: string | null;
  linkedinUrl: string | null;
  email: string | null;
  country: string | null;
  language: Language;
  assignee: Rep | null;   // null until given to a rep
  addedAt: string;
  stage: Stage;
  touches: Touches;
  lastTouchAt: string | null;
  campaigns: string[];
  flags: Flag[];
  manualCallAt: string | null;
  manualCallBy: string | null;
  manualMeetingAt: string | null;
  manualMeetingBy: string | null;
  timeline: TimelineEvent[];
}

export interface PilotSummary {
  id: string;
  bucketId: string;
  companyName: string;
  companyDomain: string | null;
  attioCompanyId: string | null;
  country: string | null;
  languageOverride: Language | null;
  language: Language | null;
  createdBy: string;
  createdAt: string;
  people: number;
  byRep: { rep: Rep; people: number }[];
  unassigned: number;
  byLanguage: Partial<Record<Language, number>>;
  byStage: Record<Stage, number>;
  replied: number;
  meetings: number;
  lastTouchAt: string | null;
  flagged: number;
}

export interface PilotDetail extends PilotSummary { persons: PilotPerson[] }

export interface CompanyOption { attioCompanyId: string; name: string; domain: string | null }

export interface PersonSuggestion {
  fullName: string;
  jobTitle: string | null;
  linkedinUrl: string | null;
  email: string | null;
  sources: ('lemlist' | 'attio')[];
  currentSender: string | null;
  campaigns: string[];
  alreadyInPilot: boolean;
}

export interface NewPilotPerson {
  fullName: string;
  jobTitle?: string | null;
  linkedinUrl?: string | null;
  email?: string | null;
  country?: string | null;
  assigneeEmail?: string | null;
}

/** A company row, with or without a person on it. */
export interface ImportRow extends Omit<NewPilotPerson, 'fullName'> { companyName: string; companyDomain?: string | null; companyCountry?: string | null; fullName?: string | null }
/** `customers`: companies left out because they are current customers; outreach never prospects them. */
export interface ImportResult { companiesCreated: number; companiesMatched: number; added: number; alreadyInPilot: string[]; noIdentifier: string[]; customers: string[] }

// ── Buckets ─────────────────────────────────────────────────────────────────

export const BUCKET_STATUSES = ['planned', 'active', 'done'] as const;
export type BucketStatus = (typeof BUCKET_STATUSES)[number];
export type AdsStage = 'not_briefed' | 'briefed' | 'created' | 'published';

export interface ChecklistItem { id: string; key: string | null; label: string; position: number; ownerEmail: string | null; doneAt: string | null; doneBy: string | null }
export interface TeamMember { email: string; name: string }
export interface LemlistCampaignRef { id: string; name: string | null; status: string | null; url: string }
export interface AdCampaignStatus {
  platform: string; campaignId: string; name: string | null;
  platformStatus: string | null; published: boolean; silent: boolean; lastSyncedDate: string | null;
}
export interface BucketResource { id: string; title: string; kind: string; url: string | null; description: string | null; available: boolean }
export interface BucketFunnel { people: number; reached: number; accepted: number; discussions: number; meetings: number }
export interface BucketMeeting {
  at: string; meetingStartAt: string; name: string | null; email: string | null;
  company: string | null; match: 'person' | 'domain' | 'manual'; source: 'calendly' | 'attio' | 'manual';
}

export interface BucketSummary {
  id: string;
  name: string;
  status: BucketStatus;
  startedAt: string | null;
  createdBy: string;
  createdAt: string;
  companies: number;
  checklist: { done: number; total: number };
  adsStage: AdsStage;
  resources: { available: number; total: number };
  byRep: { rep: Rep; people: number }[];
  unassigned: number;
  byLanguage: Partial<Record<Language, { people: number; unassigned: number }>>;
  funnel: BucketFunnel;
  lastTouchAt: string | null;
  flagged: number;
  blocking: string[];      // what stops the plan running as it stands; empty when nothing
}

export interface BucketDetail extends BucketSummary {
  checklistItems: ChecklistItem[];
  lemlistCampaigns: LemlistCampaignRef[];
  adCampaigns: AdCampaignStatus[];
  resourceItems: BucketResource[];
  meetingsList: BucketMeeting[];
  companyList: PilotDetail[];
}

export interface BucketOptions {
  lemlistCampaigns: { id: string; name: string | null; status: string | null }[];
  adCampaigns: { platform: string; campaignId: string; name: string | null; status: string | null }[];
  resources: { id: string; title: string; kind: string; available: boolean }[];
}

export interface BucketUpdate {
  name?: string;
  status?: BucketStatus;
  lemlistCampaignIds?: string[];
  adCampaigns?: { platform: string; campaignId: string }[];
  resourceIds?: string[];
}

const enc = encodeURIComponent;

export const getBuckets = () => apiClient.get<{ buckets: BucketSummary[] }>('/pilots/buckets');
export const getBucket = (id: string) => apiClient.get<BucketDetail>(`/pilots/buckets/${enc(id)}`);
export const getBucketOptions = () => apiClient.get<BucketOptions>('/pilots/buckets/options');
export const createBucket = (name: string) => apiClient.post<{ id: string }>('/pilots/buckets', { name });
export const updateBucket = (id: string, body: BucketUpdate) => apiClient.patch<{ ok: true }>(`/pilots/buckets/${enc(id)}`, body);
export const addChecklistItem = (bucketId: string, label: string) =>
  apiClient.post<{ id: string }>(`/pilots/buckets/${enc(bucketId)}/checklist`, { label });
export const updateChecklistItem = (bucketId: string, itemId: string, body: { done?: boolean; ownerEmail?: string | null }) =>
  apiClient.patch<{ ok: true }>(`/pilots/buckets/${enc(bucketId)}/checklist/${enc(itemId)}`, body);
export const getTeam = () => apiClient.get<{ team: TeamMember[] }>('/pilots/team');
export const removeChecklistItem = (bucketId: string, itemId: string) =>
  apiClient.delete<{ ok: true }>(`/pilots/buckets/${enc(bucketId)}/checklist/${enc(itemId)}`);

// ── Companies and people ────────────────────────────────────────────────────

export const getReps = () => apiClient.get<{ reps: Rep[] }>('/pilots/reps');
export const setRepRole = (email: string, role: RepSetting | null) =>
  apiClient.patch<{ ok: true }>(`/pilots/reps/${enc(email)}/role`, { role });
export const searchCompanies = (q: string) => apiClient.get<{ companies: CompanyOption[] }>(`/pilots/companies?q=${enc(q)}`);
export const getSuggestions = (id: string) =>
  apiClient.get<{ suggestions: PersonSuggestion[]; attioError: string | null }>(`/pilots/${enc(id)}/suggestions`);
export const createPilot = (bucketId: string, body: { companyName: string; companyDomain: string | null; attioCompanyId: string | null }) =>
  apiClient.post<{ id: string }>(`/pilots/buckets/${enc(bucketId)}/companies`, body);
export const setCompanyLanguage = (id: string, languageOverride: ChoosableLanguage | null) =>
  apiClient.patch<{ ok: true }>(`/pilots/${enc(id)}`, { languageOverride });
export const removePilot = (id: string) => apiClient.delete<{ ok: true }>(`/pilots/${enc(id)}`);
export const addPilotPeople = (id: string, people: NewPilotPerson[]) =>
  apiClient.post<{ added: number; alreadyInPilot: string[]; noIdentifier: string[] }>(`/pilots/${enc(id)}/people`, { people });
export const importBucketList = (bucketId: string, rows: ImportRow[]) =>
  apiClient.post<ImportResult>(`/pilots/buckets/${enc(bucketId)}/import`, { rows });
export const assignPeople = (bucketId: string, personIds: string[], assigneeEmail: string | null) =>
  apiClient.patch<{ updated: number }>(`/pilots/buckets/${enc(bucketId)}/people`, { personIds, assigneeEmail });
export const updatePilotPerson = (id: string, personId: string, body: { assigneeEmail?: string | null; manualCall?: boolean; manualMeeting?: boolean }) =>
  apiClient.patch<{ ok: true }>(`/pilots/${enc(id)}/people/${enc(personId)}`, body);
export const removePilotPerson = (id: string, personId: string) =>
  apiClient.delete<{ ok: true }>(`/pilots/${enc(id)}/people/${enc(personId)}`);
